import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Truck, MapPin, Calendar, AlertTriangle, CheckCircle, Users, Building2, Plus, Filter, Activity, Star, Sparkles, Eye, ChevronDown, ChevronRight } from "lucide-react";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SettingsDropdown } from "./SettingsDropdown";
import { LoadAcceptanceCard } from "./LoadAcceptanceCard";
import { Sentry } from "@/lib/sentry";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useRouteDisplay } from '@/utils/routeUtils';
import { useLanguage } from "@/contexts/LanguageContext";
// Notification will be automatically queued by database trigger
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { formatAppDate } from "@/lib/utils";

interface LoadRoute {
  id: string;
  title: string;
  pickup_address: string;
  pickup_city: string;
  pickup_state: string;
  pickup_date: string;
  delivery_address: string;
  delivery_city: string;
  delivery_state: string;
  delivery_date: string;
  vehicle_type?: string;
  vehicles_needed: number;
  vehicles_assigned: number;
  vehicles_active: number;
  status: string;
  created_at: string;
  load_orders: {
    id: string;
    title: string;
    customer_org_id: string;
    created_by: string;
    cargo_type: string;
    cargo_desc: string;
    cargo_weight: number;
    vehicles_needed: number;
    vehicles_assigned: number;
    vehicles_active: number;
    organizations: {
      name: string;
    };
  };
}

interface LoadAssignment {
  id: string;
  load_route_id: string;
  carrier_org_id?: string;
  customer_org_id?: string;
  status: string;
  price_per_truck?: number;
  total?: number;
  currency?: string;
  vehicles_accepted?: number;
  vehicles_assigned?: number;
  vehicles_completed?: number;
  vehicles_active?: number;
  vehicles_canceled?: number;
  carrier_notes?: string;
  assigned_at: string;
  accepted_at?: string;
  load_routes?: LoadRoute; // Add the joined route data
}

interface CarrierDashboardProps {
  companyName: string;
}

export const CarrierDashboard = ({ companyName }: CarrierDashboardProps) => {
  const { t } = useTranslation();
  const { formatRoute } = useRouteDisplay();
  const [availableLoads, setAvailableLoads] = useState<LoadRoute[]>([]);
  const [myAssignments, setMyAssignments] = useState<LoadAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingOperations, setLoadingOperations] = useState<Record<string, boolean>>({});
  const [username, setUsername] = useState<string>("");
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [isViewActiveModalOpen, setIsViewActiveModalOpen] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState<LoadRoute | null>(null);
  const [selectedActiveLoad, setSelectedActiveLoad] = useState<LoadRoute | null>(null);
  const [carrierResponses, setCarrierResponses] = useState<Record<string, { trucksCommitted: number; status: "selecting" | "accepted" }>>({});
  const [groupBy, setGroupBy] = useState<"none" | "customer" | "route">("none");
  const [activeFilter, setActiveFilter] = useState<"new" | "active" | "completed">("new");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [expandedAcceptances, setExpandedAcceptances] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const { isRTL, language } = useLanguage();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadData();
      loadUsername();
    }
  }, [user]);

  // Auto-collapse all groups when grouping is enabled
  useEffect(() => {
    if (groupBy !== "none") {
      // Get current filtered loads to determine group names
      const filteredLoads = getFilteredLoads();
      let allGroupNames: string[] = [];
      
      if (groupBy === "customer") {
        const customerNames = [...new Set(filteredLoads.map(load => load.load_orders.organizations.name))];
        allGroupNames = customerNames;
      } else if (groupBy === "route") {
        const routeKeys = [...new Set(filteredLoads.map(load => formatRoute(load.pickup_city, load.delivery_city)))];
        allGroupNames = routeKeys;
      }
      
      setCollapsedGroups(new Set(allGroupNames));
    } else {
      setCollapsedGroups(new Set());
    }
  }, [groupBy]);

  // Set up real-time listeners for load routes and assignments
  useEffect(() => {
    if (!user) return;

    // Subscribe to load_routes changes
    const routesChannel = supabase
      .channel('load-routes-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'load_routes'
        },
        (payload: any) => {
          console.log('Real-time route change:', payload);
          
          // If we have a selected load open in modal, refresh it specifically
          if (selectedLoad && payload.new && payload.new.id === selectedLoad.id) {
            refreshSelectedLoad();
          }
          if (selectedActiveLoad && payload.new && payload.new.id === selectedActiveLoad.id) {
            refreshSelectedActiveLoad();
          }
          
          // Handle different event types for routes
          if (payload.eventType === 'UPDATE' && payload.new) {
            updateRouteInData(payload.new);
          } else if (payload.eventType === 'INSERT' && payload.new) {
            addRouteToData(payload.new);
          } else if (payload.eventType === 'DELETE' && payload.old) {
            removeRouteFromData(payload.old);
          }
          
          // Force state update to recalculate counters
          setAvailableLoads(prev => [...prev]);
        }
      )
      .subscribe();

    // Subscribe to load_assignments changes
    const assignmentsChannel = supabase
      .channel('load-assignments-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'load_assignments'
        },
        (payload: any) => {
          console.log('Real-time assignment change:', payload);
          
          // If we have a selected load open in modal, refresh it specifically
          if (selectedLoad) {
            refreshSelectedLoad();
          }
          if (selectedActiveLoad) {
            refreshSelectedActiveLoad();
          }
          
          // Update assignments data first
          updateAssignmentsData();
          
          // Then refresh the specific route to update vehicle counters
          if (payload.new?.load_route_id) {
            refreshRouteById(payload.new.load_route_id);
          } else if (payload.old?.load_route_id) {
            refreshRouteById(payload.old.load_route_id);
          }
          
          // Force state update to recalculate assignment counters
          setMyAssignments(prev => [...prev]);
        }
      )
      .subscribe();

    // Subscribe to load_orders changes (for new loads)
    const ordersChannel = supabase
      .channel('load-orders-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'load_orders'
        },
        (payload: any) => {
          console.log('Real-time order change:', payload);
          
          // When a new load order is created, we need to refresh available loads
          // to catch any new routes that might have been created
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            refreshAvailableLoads();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(routesChannel);
      supabase.removeChannel(assignmentsChannel);
      supabase.removeChannel(ordersChannel);
    };
  }, [user, selectedLoad, selectedActiveLoad]);

  // Function to refresh selected load data in real-time
  const refreshSelectedLoad = async () => {
    if (!selectedLoad) return;
    
    try {
      const { data: updatedLoad, error } = await supabase
        .from('load_routes')
        .select(`
          *,
          load_orders!inner(
            id,
            title,
            customer_org_id,
            created_by,
            cargo_type,
            cargo_desc,
            cargo_weight,
            vehicles_needed,
            vehicles_assigned,
            vehicles_active,
            organizations!inner(name)
          )
        `)
        .eq('id', selectedLoad.id)
        .single();

      if (error) throw error;
      if (updatedLoad) {
        setSelectedLoad(updatedLoad);
      }
    } catch (error) {
      console.error('Error refreshing selected load:', error);
    }
  };

  // Function to refresh selected active load data in real-time
  const refreshSelectedActiveLoad = async () => {
    if (!selectedActiveLoad) return;
    
    try {
      const { data: updatedLoad, error } = await supabase
        .from('load_routes')
        .select(`
          *,
          load_orders!inner(
            id,
            title,
            customer_org_id,
            created_by,
            cargo_type,
            cargo_desc,
            cargo_weight,
            vehicles_needed,
            vehicles_assigned,
            vehicles_active,
            organizations!inner(name)
          )
        `)
        .eq('id', selectedActiveLoad.id)
        .single();

      if (error) throw error;
      if (updatedLoad) {
        setSelectedActiveLoad(updatedLoad);
      }
    } catch (error) {
      console.error('Error refreshing selected active load:', error);
    }
  };

  // Helper functions to update data in place without full refresh
  const updateRouteInData = (updatedRoute: any) => {
    setAvailableLoads(prev => {
      // Calculate vehicles_available properly: vehicles_needed - vehicles_assigned
      const vehiclesAvailable = (updatedRoute.vehicles_needed || 0) - (updatedRoute.vehicles_assigned || 0);
      
      // If the route has no available vehicles, remove it from the list
      if (vehiclesAvailable <= 0) {
        return prev.filter(route => route.id !== updatedRoute.id);
      }
      
      // Otherwise, update the existing route with calculated vehicles_available
      const updated = { ...updatedRoute, vehicles_available: vehiclesAvailable };
      return prev.map(route => 
        route.id === updatedRoute.id ? { ...route, ...updated } : route
      );
    });
  };

  const addRouteToData = async (newRoute: any) => {
    // Fetch full route data with load_orders and check if it's from a connected customer
    try {
      // Get user's organization IDs
      const { data: userOrgsAdd } = await supabase.rpc('get_user_organizations', { 
        user_uuid: user.id 
      });
      
      const orgIdsAdd = userOrgsAdd?.map(org => org.organization_id) || [];
      
      if (orgIdsAdd.length === 0) return;
      
      // Get connected customer organization IDs
      const { data: connections, error: connectionsError } = await supabase
        .from('connections')
        .select('inviter_org_id, invitee_org_id')
        .eq('status', 'active')
        .or(`inviter_org_id.in.(${orgIdsAdd.join(',')}),invitee_org_id.in.(${orgIdsAdd.join(',')})`);
      
      if (connectionsError) return;
      
      // Extract connected customer org IDs
      const connectedCustomerOrgIds = new Set<string>();
      connections?.forEach(conn => {
        if (orgIdsAdd.includes(conn.inviter_org_id)) {
          connectedCustomerOrgIds.add(conn.invitee_org_id);
        }
        if (orgIdsAdd.includes(conn.invitee_org_id)) {
          connectedCustomerOrgIds.add(conn.inviter_org_id);
        }
      });
      
      const connectedOrgIdsArray = Array.from(connectedCustomerOrgIds);
      
      if (connectedOrgIdsArray.length === 0) return;
      
      const { data: fullRoute, error } = await supabase
        .from('load_routes')
        .select(`
          *,
          load_orders!inner(
            id,
            title,
            customer_org_id,
            created_by,
            cargo_type,
            cargo_desc,
            cargo_weight,
            vehicles_needed,
            vehicles_assigned,
            vehicles_active,
            organizations!inner(name)
          )
        `)
        .eq('id', newRoute.id)
        .eq('status', 'published')
        .in('load_orders.customer_org_id', connectedOrgIdsArray)
        .single();

      if (!error && fullRoute) {
        // Calculate vehicles_available and only add if > 0
        const vehiclesAvailable = (fullRoute.vehicles_needed || 0) - (fullRoute.vehicles_assigned || 0);
        if (vehiclesAvailable > 0) {
          const routeWithAvailable = { ...fullRoute, vehicles_available: vehiclesAvailable };
          // Check if route already exists before adding to prevent duplicates
          setAvailableLoads(prev => {
            const existingRouteIndex = prev.findIndex(route => route.id === routeWithAvailable.id);
            if (existingRouteIndex !== -1) {
              // Route already exists, update it instead of adding
              const updated = [...prev];
              updated[existingRouteIndex] = routeWithAvailable;
              return updated;
            } else {
              // Route doesn't exist, add it
              return [routeWithAvailable, ...prev];
            }
          });
        }
      }
    } catch (error) {
      console.error('Error fetching new route:', error);
    }
  };

  const removeRouteFromData = (deletedRoute: any) => {
    setAvailableLoads(prev => prev.filter(route => route.id !== deletedRoute.id));
  };

  const updateAssignmentsData = async () => {
    if (!user) return;
    
    try {
      const { data: userOrgsUpdate } = await supabase.rpc('get_user_organizations', { 
        user_uuid: user.id 
      });
      
      const orgIdsUpdate = userOrgsUpdate?.map(org => org.organization_id) || [];
      
      const { data: assignments, error } = await supabase
        .from('load_assignments')
        .select(`
          *, 
          vehicles_accepted,
          vehicles_completed, 
          vehicles_active,
          load_routes!fk_load_assignments_load_route_id(
            *,
            load_orders!inner(
              id,
              title,
              customer_org_id,
              created_by,
              cargo_type,
              cargo_desc,
              cargo_weight,
              vehicles_needed,
              vehicles_assigned,
              vehicles_active,
              organizations!inner(name)
            )
          )
        `)
        .in('carrier_org_id', orgIdsUpdate)
        .order('assigned_at', { ascending: false });

      if (!error) {
        setMyAssignments((assignments as unknown) as LoadAssignment[]);
        // Force a re-render to recalculate counters
        setMyAssignments(prev => [...prev]);
      }
    } catch (error) {
      console.error('Error updating assignments:', error);
    }
  };

  // Helper function to refresh a specific route by ID
  const refreshRouteById = async (routeId: string) => {
    try {
      const { data: updatedRoute, error } = await supabase
        .from('load_routes')
        .select(`
          *,
          load_orders!inner(
            id,
            title,
            customer_org_id,
            created_by,
            cargo_type,
            cargo_desc,
            cargo_weight,
            vehicles_needed,
            vehicles_assigned,
            vehicles_active,
            organizations!inner(name)
          )
        `)
        .eq('id', routeId)
        .single();

      if (!error && updatedRoute) {
        updateRouteInData(updatedRoute);
        // Force a re-render to recalculate counters
        setAvailableLoads(prev => [...prev]);
      }
    } catch (error) {
      console.error('Error refreshing route:', error);
    }
  };

  // Helper function to refresh available loads (for new load orders)
  const refreshAvailableLoads = async () => {
    if (!user) return;
    
    try {
      // Get user's organization IDs
      const { data: userOrgsRefresh } = await supabase.rpc('get_user_organizations', { 
        user_uuid: user.id 
      });
      
      const orgIdsRefresh = userOrgsRefresh?.map(org => org.organization_id) || [];
      
      if (orgIdsRefresh.length === 0) {
        setAvailableLoads([]);
        return;
      }
      
      // Get connected customer organization IDs
      const { data: connections, error: connectionsError } = await supabase
        .from('connections')
        .select('inviter_org_id, invitee_org_id')
        .eq('status', 'active')
        .or(`inviter_org_id.in.(${orgIdsRefresh.join(',')}),invitee_org_id.in.(${orgIdsRefresh.join(',')})`);
      
      if (connectionsError) throw connectionsError;
      
      // Extract connected customer org IDs (excluding our own org IDs)
      const connectedCustomerOrgIds = new Set<string>();
      connections?.forEach(conn => {
        if (orgIdsRefresh.includes(conn.inviter_org_id)) {
          connectedCustomerOrgIds.add(conn.invitee_org_id);
        }
        if (orgIdsRefresh.includes(conn.invitee_org_id)) {
          connectedCustomerOrgIds.add(conn.inviter_org_id);
        }
      });
      
      // Convert to array for the query
      const connectedOrgIdsArray = Array.from(connectedCustomerOrgIds);
      
      // Load available routes from connected customers only
      let loadsQuery = supabase
        .from('load_routes')
        .select(`
          *,
          load_orders!inner(
            id,
            title,
            customer_org_id,
            created_by,
            cargo_type,
            cargo_desc,
            cargo_weight,
            vehicles_needed,
            vehicles_assigned,
            vehicles_active,
            organizations!inner(name)
          )
        `)
        .eq('status', 'published')
        .order('created_at', { ascending: false });
      
      // Filter by connected customer organizations if any exist
      if (connectedOrgIdsArray.length > 0) {
        loadsQuery = loadsQuery.in('load_orders.customer_org_id', connectedOrgIdsArray);
      } else {
        // If no connections, return empty array
        setAvailableLoads([]);
        return;
      }
      
      const { data: loads, error } = await loadsQuery;

      if (!error) {
        // Filter out loads with no available vehicles and add vehicles_available calculation
        const availableLoadsFiltered = (loads || []).map(load => ({
          ...load,
          vehicles_available: Math.max(0, (load.vehicles_needed || 0) - (load.vehicles_assigned || 0))
        })).filter(load => load.vehicles_available > 0);
        
        setAvailableLoads(availableLoadsFiltered);
      }
    } catch (error) {
      console.error('Error refreshing available loads:', error);
    }
  };

  // Helper function to toggle group collapse state
  const toggleGroupCollapse = (groupName: string) => {
    setCollapsedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const loadData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Get user's organization IDs
      const { data: userOrgs } = await supabase.rpc('get_user_organizations', { 
        user_uuid: user.id 
      });
      
      const orgIds = userOrgs?.map(org => org.organization_id) || [];
      
      if (orgIds.length === 0) {
        setAvailableLoads([]);
        setMyAssignments([]);
        return;
      }
      
      // Get connected customer organization IDs
      const { data: connections, error: connectionsError } = await supabase
        .from('connections')
        .select('inviter_org_id, invitee_org_id')
        .eq('status', 'active')
        .or(`inviter_org_id.in.(${orgIds.join(',')}),invitee_org_id.in.(${orgIds.join(',')})`);
      
      if (connectionsError) throw connectionsError;
      
      // Extract connected customer org IDs (excluding our own org IDs)
      const connectedCustomerOrgIds = new Set<string>();
      connections?.forEach(conn => {
        if (orgIds.includes(conn.inviter_org_id)) {
          connectedCustomerOrgIds.add(conn.invitee_org_id);
        }
        if (orgIds.includes(conn.invitee_org_id)) {
          connectedCustomerOrgIds.add(conn.inviter_org_id);
        }
      });
      
      // Convert to array for the query
      const connectedOrgIdsArray = Array.from(connectedCustomerOrgIds);
      
      // Load available routes from connected customers only
      let loadsQuery = supabase
        .from('load_routes')
        .select(`
          *,
          load_orders!inner(
            id,
            title,
            customer_org_id,
            created_by,
            cargo_type,
            cargo_desc,
            cargo_weight,
            vehicles_needed,
            vehicles_assigned,
            vehicles_active,
            organizations!inner(name)
          )
        `)
        .eq('status', 'published')
        .order('created_at', { ascending: false });
      
      // Filter by connected customer organizations if any exist
      if (connectedOrgIdsArray.length > 0) {
        loadsQuery = loadsQuery.in('load_orders.customer_org_id', connectedOrgIdsArray);
        const { data: loads, error: loadsError } = await loadsQuery;
        if (loadsError) throw loadsError;
        
        // Filter out loads with no available vehicles and add vehicles_available calculation
        const availableLoadsFiltered = (loads || []).map(load => ({
          ...load,
          vehicles_available: Math.max(0, (load.vehicles_needed || 0) - (load.vehicles_assigned || 0))
        })).filter(load => load.vehicles_available > 0);
        
        setAvailableLoads(availableLoadsFiltered);
      } else {
        // If no connections, set empty array without error
        setAvailableLoads([]);
      }

      // Load my assignments - using carrier_org_id since carrier_id was deprecated      
      const { data: assignments, error: assignmentsError } = await supabase
        .from('load_assignments')
        .select(`
          *, 
          vehicles_accepted,
          vehicles_completed, 
          vehicles_active,
          load_routes!fk_load_assignments_load_route_id(
            *,
            load_orders!inner(
              id,
              title,
              customer_org_id,
              created_by,
              cargo_type,
              cargo_desc,
              cargo_weight,
              vehicles_needed,
              vehicles_assigned,
              vehicles_active,
              organizations!inner(name)
            )
          )
        `)
        .in('carrier_org_id', orgIds)
        .order('assigned_at', { ascending: false });

      if (assignmentsError) throw assignmentsError;

      // Assignments are handled separately - no loads variable needed here
      setMyAssignments((assignments as unknown) as LoadAssignment[]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error loading data",
        description: "Failed to load loads and assignments",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadUsername = async () => {
    if (!user) return;
    
    try {
      // Get user's first organization's username
      const { data: orgMember } = await supabase
        .from('organization_members')
        .select('org_id, organizations!fk_organization_members_organization(username)')
        .eq('user_id', user.id)
        .not('organizations.username', 'is', null)
        .limit(1)
        .single();

      if (orgMember?.organizations?.username) {
        setUsername(orgMember.organizations.username);
      }
    } catch (error) {
      console.error('Error loading username:', error);
    }
  };

  const handleAcceptLoad = (load: LoadRoute) => {
    setSelectedLoad(load);
    setIsAcceptModalOpen(true);
  };

  const handleViewActiveLoad = (load: LoadRoute) => {
    setSelectedActiveLoad(load);
    setIsViewActiveModalOpen(true);
  };

  const handleCompleteLoad = async (routeId: string, count: number) => {
    if (!user || loadingOperations[`complete-${routeId}`]) return;

    setLoadingOperations(prev => ({ ...prev, [`complete-${routeId}`]: true }));

    try {
      // Get current user's organization
      const { data: userOrgs } = await supabase.rpc('get_user_organizations', { 
        user_uuid: user.id 
      });
      
      if (!userOrgs || userOrgs.length === 0) {
        throw new Error("No organization found for user");
      }

      // Find assignments for this route and this carrier
      const carrierAssignments = myAssignments.filter(
        a => a.load_route_id === routeId && 
             a.carrier_org_id === userOrgs[0].organization_id &&
             (a.vehicles_active || 0) > 0
      );

      if (carrierAssignments.length === 0) {
        throw new Error("No active assignments found for this route");
      }

      // Use any assignment ID since backend handles multi-assignment logic
      const assignmentId = carrierAssignments[0].id;
      
      const { error } = await supabase.rpc('handle_truck_operation', {
        assignment_id_param: assignmentId,
        operation_type: 'complete',
        truck_count: count
      });

      if (error) throw error;

      toast({
        title: "Load completed",
        description: `You have successfully completed ${count} truck(s) for this load`
      });

      // Real-time updates will handle data refresh automatically
      // Don't reload data or close modal - let real-time do its work
      
      // Track successful completion
      Sentry.addBreadcrumb({
        message: `Successfully completed ${count} trucks`,
        category: 'business_success',
        level: 'info',
        data: { routeId, count, operation: 'complete' }
      });
      
    } catch (error) {
      console.error('Error completing load:', error);
      
      // Capture business-critical error
      Sentry.captureException(error, {
        tags: {
          operation: 'complete_trucks',
          critical: true,
          business_process: 'load_completion'
        },
        extra: {
          routeId,
          count,
          userId: user.id,
          assignmentCount: myAssignments.filter(a => a.load_route_id === routeId).length
        }
      });
      
      toast({
        title: "Error completing load",
        description: "Failed to complete the load",
        variant: "destructive"
      });
    } finally {
      setLoadingOperations(prev => {
        const updated = { ...prev };
        delete updated[`complete-${routeId}`];
        return updated;
      });
    }
  };

  const handleCancelLoad = async (routeId: string, count: number) => {
    if (!user || loadingOperations[`cancel-${routeId}`]) return;

    setLoadingOperations(prev => ({ ...prev, [`cancel-${routeId}`]: true }));

    try {
      // Get current user's organization
      const { data: userOrgs } = await supabase.rpc('get_user_organizations', { 
        user_uuid: user.id 
      });
      
      if (!userOrgs || userOrgs.length === 0) {
        throw new Error("No organization found for user");
      }

      // Find assignments for this route and this carrier
      const carrierAssignments = myAssignments.filter(
        a => a.load_route_id === routeId && 
             a.carrier_org_id === userOrgs[0].organization_id &&
             (a.vehicles_active || 0) > 0
      );

      if (carrierAssignments.length === 0) {
        throw new Error("No active assignments found for this route");
      }

      // Use any assignment ID since backend handles multi-assignment logic
      const assignmentId = carrierAssignments[0].id;
      
      const { error } = await supabase.rpc('handle_truck_operation', {
        assignment_id_param: assignmentId,
        operation_type: 'cancel',
        truck_count: count
      });

      if (error) throw error;

      // Notification will be sent automatically via database triggers

      toast({
        title: "Load cancelled",
        description: `You have cancelled ${count} truck(s) for this load`
      });

      // Real-time updates will handle data refresh automatically
      // Don't reload data or close modal - let real-time do its work
      
      // Track successful cancellation
      Sentry.addBreadcrumb({
        message: `Successfully canceled ${count} trucks`,
        category: 'business_success',
        level: 'info',
        data: { routeId, count, operation: 'cancel' }
      });
      
    } catch (error) {
      console.error('Error cancelling load:', error);
      
      // Capture business-critical error
      Sentry.captureException(error, {
        tags: {
          operation: 'cancel_trucks',
          critical: true,
          business_process: 'load_cancellation'
        },
        extra: {
          routeId,
          count,
          userId: user.id,
          assignmentCount: myAssignments.filter(a => a.load_route_id === routeId).length
        }
      });
      
      toast({
        title: "Error cancelling load",
        description: "Failed to cancel the load",
        variant: "destructive"
      });
    } finally {
      setLoadingOperations(prev => {
        const updated = { ...prev };
        delete updated[`cancel-${routeId}`];
        return updated;
      });
    }
  };

  const handleStartSelection = (routeId: string) => {
    // Start with 0 for smooth animation
    setCarrierResponses(prev => ({
      ...prev,
      [routeId]: { trucksCommitted: 0, status: "selecting" }
    }));
    
    // Animate to 1 with a very short delay for smooth transition
    requestAnimationFrame(() => {
      setCarrierResponses(prev => ({
        ...prev,
        [routeId]: { trucksCommitted: 1, status: "selecting" }
      }));
    });
  };

  const handleConfirmAcceptance = async (routeId: string) => {
    if (!user || !selectedLoad || loadingOperations[`accept-${routeId}`]) return;

    setLoadingOperations(prev => ({ ...prev, [`accept-${routeId}`]: true }));

    try {
      const response = carrierResponses[routeId];
      if (!response) return;

      // Get user's organization
      const { data: userOrgs } = await supabase.rpc('get_user_organizations', { 
        user_uuid: user.id 
      });
      
      if (!userOrgs || userOrgs.length === 0) {
        throw new Error("Please create or join an organization first");
      }
      
      // Create the assignment first
      const { data: assignment, error: insertError } = await supabase
        .from('load_assignments')
        .insert({
          load_route_id: routeId,
          carrier_org_id: userOrgs[0].organization_id,
          assigned_by: user.id,
          status: 'pending',
          currency: 'SAR',
          vehicles_accepted: 0,
          vehicles_assigned: 0,
          accepted_at: new Date().toISOString()
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Now use the centralized function to accept trucks
      const { error: acceptError } = await supabase.rpc('handle_truck_operation', {
        assignment_id_param: assignment.id,
        operation_type: 'accept',
        truck_count: response.trucksCommitted
      });

      if (acceptError) throw acceptError;

      // Notification will be sent automatically via database triggers

      toast({
        title: "Load accepted",
        description: `You have successfully accepted ${response.trucksCommitted} truck(s) for this load`
      });

      // Show animation first by updating state to show as confirmed
      setCarrierResponses(prev => ({
        ...prev,
        [routeId]: { ...prev[routeId], status: "accepted" as const }
      }));
      
      // Wait for animation to complete before removing
      setTimeout(() => {
        // Remove from responses and close modal
        setCarrierResponses(prev => {
          const updated = { ...prev };
          delete updated[routeId];
          return updated;
        });
        
        setIsAcceptModalOpen(false);
        setSelectedLoad(null);
        
        // Reload data
        loadData();
      }, 800); // Give time for the animation to be visible
    } catch (error) {
      console.error('Error accepting load:', error);
      toast({
        title: "Error accepting load",
        description: "Failed to accept the load request",
        variant: "destructive"
      });
    } finally {
      setLoadingOperations(prev => {
        const updated = { ...prev };
        delete updated[`accept-${routeId}`];
        return updated;
      });
    }
  };

  const handleUpdateTrucks = (routeId: string, trucks: number) => {
    setCarrierResponses(prev => ({
      ...prev,
      [routeId]: { ...prev[routeId], trucksCommitted: trucks }
    }));
  };

  const handleCancelSelection = (routeId: string) => {
    setCarrierResponses(prev => {
      const updated = { ...prev };
      delete updated[routeId];
      return updated;
    });
  };

  const handleToggleExpandedAcceptances = (routeId: string) => {
    setExpandedAcceptances(prev => {
      const updated = new Set(prev);
      if (updated.has(routeId)) {
        updated.delete(routeId);
      } else {
        updated.add(routeId);
      }
      return updated;
    });
  };

  // Helper function to get aggregated assignment data for a route
  const getRouteAssignmentSummary = (routeId: string) => {
    const routeAssignments = myAssignments.filter(a => a.load_route_id === routeId);
    
    return {
      totalVehiclesAssigned: routeAssignments.reduce((sum, a) => sum + (a.vehicles_assigned || 0), 0),
      totalVehiclesActive: routeAssignments.reduce((sum, a) => sum + (a.vehicles_active || 0), 0),
      totalVehiclesCompleted: routeAssignments.reduce((sum, a) => sum + (a.vehicles_completed || 0), 0),
      totalVehiclesCanceled: routeAssignments.reduce((sum, a) => sum + (a.vehicles_canceled || 0), 0),
      hasAssignments: routeAssignments.length > 0,
      hasActiveAssignments: routeAssignments.some(a => (a.vehicles_active || 0) > 0)
    };
  };

  // Filter loads based on active filter
  const getFilteredLoads = () => {
    if (activeFilter === "new") {
      return availableLoads;
    } else if (activeFilter === "active") {
      // Show loads from assignments where this carrier has active trucks
      const activeAssignmentRoutes = myAssignments
        .filter(assignment => (assignment.vehicles_active || 0) > 0)
        .map(assignment => assignment.load_routes)
        .filter(route => route) // Remove any null/undefined routes
        .reduce((unique, route) => {
          // Remove duplicates based on route ID
          if (!unique.find(r => r.id === route.id)) {
            unique.push(route);
          }
          return unique;
        }, [] as any[]);
      
      return activeAssignmentRoutes;
    } else if (activeFilter === "completed") {
      // Show loads from completed assignments
      const completedAssignmentRoutes = myAssignments
        .filter(assignment => assignment.status === 'completed')
        .map(assignment => assignment.load_routes)
        .filter(route => route) // Remove any null/undefined routes
        .reduce((unique, route) => {
          // Remove duplicates based on route ID
          if (!unique.find(r => r.id === route.id)) {
            unique.push(route);
          }
          return unique;
        }, [] as any[]);
      
      return completedAssignmentRoutes;
    }
    return availableLoads;
  };

  const filteredLoads = getFilteredLoads();

  // Group loads based on selected grouping method
  const getGroupedLoads = (): Record<string, LoadRoute[]> => {
    if (groupBy === "customer") {
      const grouped = filteredLoads.reduce((acc, load) => {
        const customerName = load.load_orders.organizations.name;
        if (!acc[customerName]) {
          acc[customerName] = [];
        }
        acc[customerName].push(load);
        return acc;
      }, {} as Record<string, LoadRoute[]>);
      return grouped;
    } else if (groupBy === "route") {
      const grouped = filteredLoads.reduce((acc, load) => {
        const routeKey = formatRoute(load.pickup_city, load.delivery_city);
        if (!acc[routeKey]) {
          acc[routeKey] = [];
        }
        acc[routeKey].push(load);
        return acc;
      }, {} as Record<string, LoadRoute[]>);
      return grouped;
    }
    return { [t('carrierDashboard.allLoads')]: filteredLoads as LoadRoute[] };
  };

  const groupedLoads = getGroupedLoads();

  // Calculate stats based on truck counts with proper real-time calculations
  // New: total trucks remaining that need carriers across all load routes
  const newRoutes = availableLoads.reduce((sum, load) => {
    const vehiclesAvailable = (load.vehicles_needed || 0) - (load.vehicles_assigned || 0);
    return sum + Math.max(0, vehiclesAvailable);
  }, 0);
  
  // Active: total trucks currently active across all assignments
  const activeRoutes = myAssignments
    .filter(assignment => (assignment.vehicles_active || 0) > 0)
    .reduce((sum, assignment) => sum + (assignment.vehicles_active || 0), 0);
  
  // Completed: total trucks completed across all assignments
  const completedRoutes = myAssignments
    .reduce((sum, assignment) => sum + (assignment.vehicles_completed || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={`relative min-h-screen ${isRTL ? 'rtl' : 'ltr'} overflow-x-hidden`} dir={isRTL ? 'rtl' : 'ltr'} style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="max-w-6xl mx-auto p-3 sm:p-4 space-y-4 sm:space-y-6 pb-20 sm:pb-4 min-h-screen">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {isRTL ? (
            <>
              {/* Settings section - left side in RTL */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <SettingsDropdown />
              </div>
              
              {/* Logo section - right side in RTL */}
              <div className="flex items-center gap-3 sm:gap-4 flex-row-reverse">
                <div className="min-w-0">
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground truncate text-right">{t('carrierDashboard.title')}</h1>
                  <p className="text-sm sm:text-base text-muted-foreground truncate text-right">{companyName} • {t('carrierDashboard.activeLoads')}</p>
                </div>
                <div className="bg-primary px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg flex-shrink-0">
                  <img src="/lovable-uploads/94433138-910b-4d0f-a142-73e70d167ac6.png" alt="Shohnat" className="h-5 sm:h-6 w-auto" />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Logo section - left side in LTR */}
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="bg-primary px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg flex-shrink-0">
                  <img src="/lovable-uploads/94433138-910b-4d0f-a142-73e70d167ac6.png" alt="Shohnat" className="h-5 sm:h-6 w-auto" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground truncate text-left">{t('carrierDashboard.title')}</h1>
                  <p className="text-sm sm:text-base text-muted-foreground truncate text-left">{companyName} • {t('carrierDashboard.activeLoads')}</p>
                </div>
              </div>
              
              {/* Settings section - right side in LTR */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <SettingsDropdown />
              </div>
            </>
          )}
        </div>

        {/* Stats Cards Row - Clickable Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card 
            className={`cursor-pointer transition-all duration-200 ${
              activeFilter === "new" 
                ? "bg-gradient-to-br from-accent-green to-accent-blue text-white shadow-xl" 
                : "hover:shadow-md bg-card"
            }`}
            onClick={() => setActiveFilter("new")}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold mb-1">{newRoutes}</p>
                  <p className="text-sm opacity-90">{t('carrierDashboard.newTrucks')}</p>
                </div>
                <div className={`p-3 rounded-full ${
                  activeFilter === "new" ? "bg-white/20" : "bg-muted"
                }`}>
                  <Sparkles className={`h-6 w-6 ${
                    activeFilter === "new" ? "text-white" : "text-muted-foreground"
                  }`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all duration-200 ${
              activeFilter === "active" 
                ? "bg-navy-blue text-white shadow-xl" 
                : "hover:shadow-md bg-card"
            }`}
            onClick={() => setActiveFilter("active")}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold mb-1">{activeRoutes}</p>
                  <p className="text-sm opacity-90">{t('carrierDashboard.activeTrucks')}</p>
                </div>
                <div className={`p-3 rounded-full ${
                  activeFilter === "active" ? "bg-white/20" : "bg-muted"
                }`}>
                  <Truck className={`h-6 w-6 ${
                    activeFilter === "active" ? "text-white" : "text-muted-foreground"
                  }`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all duration-200 ${
              activeFilter === "completed" 
                ? "bg-green-600 text-white shadow-xl" 
                : "hover:shadow-md bg-card"
            }`}
            onClick={() => setActiveFilter("completed")}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold mb-1">{completedRoutes}</p>
                  <p className="text-sm opacity-90">{t('carrierDashboard.completedTrucks')}</p>
                </div>
                <div className={`p-3 rounded-full ${
                  activeFilter === "completed" ? "bg-white/20" : "bg-muted"
                }`}>
                  <CheckCircle className={`h-6 w-6 ${
                    activeFilter === "completed" ? "text-white" : "text-muted-foreground"
                  }`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content - Load Opportunities Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
              <div>
                <CardTitle className="text-lg sm:text-xl font-bold">{t('carrierDashboard.loadOpportunitiesTitle')}</CardTitle>
                <p className="text-xs sm:text-sm text-muted-foreground">{t('carrierDashboard.routeOpportunities')}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select value={groupBy} onValueChange={(value: "none" | "customer" | "route") => setGroupBy(value)}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder={t('carrierDashboard.groupBy')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('carrierDashboard.noGrouping')}</SelectItem>
                    <SelectItem value="customer">{t('carrierDashboard.groupByCustomer')}</SelectItem>
                    <SelectItem value="route">{t('carrierDashboard.groupByRoute')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {filteredLoads.length === 0 ? (
              <div className="p-8 text-center">
                <Truck className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {t('carrierDashboard.noAvailableLoads')}
                </h3>
                <p className="text-muted-foreground">
                  {t('carrierDashboard.checkBackLater')}
                </p>
              </div>
            ) : (
              <>
                {/* Mobile Card Layout */}
                <div className="block md:hidden space-y-4">
                {Object.entries(groupedLoads).map(([groupName, loads]) => (
                  <div key={groupName} className="space-y-4">
                     {groupBy !== "none" && (
                       <div className="px-4 sm:px-6 py-3 bg-muted/30 border-b cursor-pointer hover:bg-muted/50 transition-all duration-200 rounded-lg" onClick={() => toggleGroupCollapse(groupName)}>
                         <div className="flex items-center justify-between">
                           <div className="flex items-center gap-2">
                             <div className="transition-transform duration-200">
                               {collapsedGroups.has(groupName) ? (
                                 <ChevronRight className="h-4 w-4 text-muted-foreground" />
                               ) : (
                                 <ChevronDown className="h-4 w-4 text-muted-foreground" />
                               )}
                             </div>
                             <h3 className="font-semibold text-base sm:text-lg">{groupName}</h3>
                           </div>
                           <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
                             <span className="flex items-center gap-1">
                               <Truck className="h-3 w-3 sm:h-4 sm:w-4" />
                               {t('carrierDashboard.trucksAvailableCount', { count: loads.reduce((sum, load) => sum + ((load.vehicles_needed || 0) - (load.vehicles_assigned || 0)), 0) })}
                             </span>
                             <span>{t('carrierDashboard.routesCount', { count: loads.length })}</span>
                           </div>
                         </div>
                       </div>
                     )}
                     
                     <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                       collapsedGroups.has(groupName) ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
                     }`}>
                       {!collapsedGroups.has(groupName) && (
                         <div className="space-y-3">
                           {loads.map((load) => (
                             <Card key={load.id} className="border border-border hover:shadow-md transition-shadow">
                               <CardContent className="p-5 sm:p-6">
                                 <div className="space-y-3">
                                   {/* Route and Customer Info */}
                                   <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                     <div className="space-y-3">
                                       {groupBy !== "route" && (
                                          <div className="flex items-center gap-2">
                                            <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                            <div>
                                              <span className="font-medium truncate">{formatRoute(load.pickup_city, load.delivery_city)}</span>
                                              <div className="text-xs text-muted-foreground">
                                                {load.id.substring(0, 8).toUpperCase()}
                                              </div>
                                            </div>
                                          </div>
                                       )}
                                       {(groupBy !== "customer" && groupBy !== "route") || groupBy === "route" ? (
                                         <div className="flex items-center gap-1 text-gray-600 font-medium mt-6">
                                           <Users className="h-3 w-3 text-primary" />
                                           {load.load_orders.organizations.name}
                                         </div>
                                       ) : null}
                                     </div>
                                   </div>

                                   {/* Stats Row */}
                                   <div className="flex flex-wrap items-center gap-4 text-sm">
                                     <div className="flex items-center gap-2">
                                       <Truck className="h-4 w-4 text-muted-foreground" />
                                       <span className="text-green-600 font-semibold">
                                          {activeFilter === "active" 
                                            ? getRouteAssignmentSummary(load.id).totalVehiclesActive
                                            : activeFilter === "completed"
                                            ? getRouteAssignmentSummary(load.id).totalVehiclesCompleted
                                            : (load.vehicles_needed || 0) - (load.vehicles_assigned || 0)
                                          }
                                       </span>
                                       <span className="text-muted-foreground">
                                         {activeFilter === "new" ? "available" : activeFilter === "active" ? "active" : "completed"}
                                       </span>
                                     </div>
                                     <div className="flex items-center gap-2">
                                       <Calendar className="h-4 w-4 text-muted-foreground" />
                                       <span className="text-muted-foreground">
                                         {formatAppDate(load.pickup_date, language)}
                                       </span>
                                     </div>
                                   </div>

                                   {/* Action Button */}
                                   <div className="flex justify-end pt-2">
                                     {activeFilter === "new" && (
                                       <Button
                                         size="sm"
                                         onClick={() => handleAcceptLoad(load)}
                                         className="text-white bg-gradient-to-r from-accent-green to-accent-blue hover:from-accent-green/90 hover:to-accent-blue/90 w-full sm:w-auto"
                                       >
                                         <Plus className="h-4 w-4 mr-2" />
                                         {t('carrierDashboard.acceptLoads')}
                                       </Button>
                                     )}
                                     {activeFilter === "active" && (
                                       <Button
                                         size="sm"
                                         onClick={() => handleViewActiveLoad(load)}
                                         variant="outline"
                                         className="text-primary border-primary hover:bg-primary hover:text-white w-full sm:w-auto"
                                       >
                                         <Eye className="h-4 w-4 mr-2" />
                                         {t('carrierDashboard.view')}
                                       </Button>
                                     )}
                                   </div>
                                 </div>
                               </CardContent>
                             </Card>
                           ))}
                         </div>
                       )}
                     </div>
                   </div>
                ))}
              </div>

              {/* Desktop Table Layout */}
              <div className="hidden md:block overflow-x-auto">
                {Object.entries(groupedLoads).map(([groupName, loads]) => (
                  <div key={groupName} className="mb-6 last:mb-0">
                     {groupBy !== "none" && (
                       <div className="px-6 py-3 bg-muted/30 border-b cursor-pointer hover:bg-muted/50 transition-all duration-200" onClick={() => toggleGroupCollapse(groupName)}>
                         <div className="flex items-center justify-between">
                           <div className="flex items-center gap-2">
                             <div className="transition-transform duration-200">
                               {collapsedGroups.has(groupName) ? (
                                 <ChevronRight className="h-4 w-4 text-muted-foreground" />
                               ) : (
                                 <ChevronDown className="h-4 w-4 text-muted-foreground" />
                               )}
                             </div>
                             <h3 className="font-semibold text-lg">{groupName}</h3>
                           </div>
                           <div className="flex items-center gap-4 text-sm text-muted-foreground">
                             <span className="flex items-center gap-1">
                               <Truck className="h-4 w-4" />
                               {t('carrierDashboard.trucksAvailableCount', { count: loads.reduce((sum, load) => sum + ((load.vehicles_needed || 0) - (load.vehicles_assigned || 0)), 0) })}
                             </span>
                             <span>{t('carrierDashboard.routesCount', { count: loads.length })}</span>
                           </div>
                         </div>
                       </div>
                     )}
                     
                     <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                       collapsedGroups.has(groupName) ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
                     }`}>
                       {!collapsedGroups.has(groupName) && (
                       <Table>
                         {groupBy === "none" || Object.keys(groupedLoads).indexOf(groupName) === 0 ? (
                            <TableHeader>
                              <TableRow>
                                {groupBy !== "route" && <TableHead>{t('carrierDashboard.route')}</TableHead>}
                                {groupBy === "route" && <TableHead>{t('carrierDashboard.customer')}</TableHead>}
                                <TableHead>{t('carrierDashboard.available')}</TableHead>
                                <TableHead>{t('carrierDashboard.earliestPickup')}</TableHead>
                                {groupBy !== "customer" && groupBy !== "route" && <TableHead>{t('carrierDashboard.customer')}</TableHead>}
                                <TableHead>{t('carrierDashboard.actions')}</TableHead>
                              </TableRow>
                            </TableHeader>
                         ) : null}
                         <TableBody>
                           {loads.map((load) => (
                             <TableRow key={load.id}>
                               {groupBy !== "route" && (
                                 <TableCell>
                                    <div className="flex items-center gap-2">
                                      <MapPin className="h-4 w-4 text-muted-foreground" />
                                      <div>
                                        <span>{formatRoute(load.pickup_city, load.delivery_city)}</span>
                                        <div className="text-xs text-muted-foreground">
                                          {load.id.substring(0, 8).toUpperCase()}
                                        </div>
                                      </div>
                                    </div>
                                 </TableCell>
                               )}
                               {groupBy === "route" && (
                                 <TableCell>
                                   <div className="flex items-center gap-2">
                                     <Users className="h-4 w-4 text-muted-foreground" />
                                     <span className="text-sm">{load.load_orders.organizations.name}</span>
                                   </div>
                                 </TableCell>
                               )}
                               <TableCell>
                                 <div className="flex items-center gap-2">
                                   <Truck className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-green-600 font-semibold">
                                       {activeFilter === "active" 
                                         ? getRouteAssignmentSummary(load.id).totalVehiclesActive
                                         : activeFilter === "completed"
                                         ? getRouteAssignmentSummary(load.id).totalVehiclesCompleted
                                         : (load.vehicles_needed || 0) - (load.vehicles_assigned || 0)
                                       }
                                    </span>
                                 </div>
                               </TableCell>
                               <TableCell>
                                 <div className="flex items-center gap-2">
                                   <Calendar className="h-4 w-4 text-muted-foreground" />
                                   <span className="text-sm">
                                     {formatAppDate(load.pickup_date, language)}
                                   </span>
                                 </div>
                               </TableCell>
                               {groupBy !== "customer" && groupBy !== "route" && (
                                 <TableCell>
                                   <div className="flex items-center gap-2">
                                     <Users className="h-4 w-4 text-muted-foreground" />
                                     <span className="text-sm">{load.load_orders.organizations.name}</span>
                                   </div>
                                 </TableCell>
                               )}
                               <TableCell>
                                 {activeFilter === "new" && (
                                   <Button
                                     size="sm"
                                     onClick={() => handleAcceptLoad(load)}
                                     className="text-white bg-gradient-to-r from-accent-green to-accent-blue hover:from-accent-green/90 hover:to-accent-blue/90"
                                   >
                                     <Plus className="h-4 w-4 mr-2" />
                                     {t('carrierDashboard.acceptLoads')}
                                   </Button>
                                 )}
                                 {activeFilter === "active" && (
                                   <Button
                                     size="sm"
                                     onClick={() => handleViewActiveLoad(load)}
                                     variant="outline"
                                     className="text-primary border-primary hover:bg-primary hover:text-white"
                                   >
                                     <Eye className="h-4 w-4 mr-2" />
                                     {t('carrierDashboard.view')}
                                   </Button>
                                 )}
                               </TableCell>
                             </TableRow>
                           ))}
                         </TableBody>
                       </Table>
                       )}
                     </div>
                   </div>
                ))}
              </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Accept Load Modal */}
        <Dialog open={isAcceptModalOpen} onOpenChange={setIsAcceptModalOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader className="pb-6 border-b border-border">
              <DialogTitle className="text-xl font-semibold">{t('carrierDashboard.acceptLoadRouteDetails')}</DialogTitle>
            </DialogHeader>
            
            {selectedLoad && (
              <div className="space-y-8 pt-6 px-2">
                <LoadAcceptanceCard
                  id={selectedLoad.id}
                  title={selectedLoad.title}
                  subtitle={formatRoute(selectedLoad.pickup_city, selectedLoad.delivery_city)}
                  loadDetail={{
                    pickupCity: selectedLoad.pickup_city,
                    dropCity: selectedLoad.delivery_city,
                    pickupDate: formatAppDate(selectedLoad.pickup_date, language),
                    vehicleType: selectedLoad.vehicle_type || 'Truck',
                    weightTons: selectedLoad.load_orders.cargo_weight ? selectedLoad.load_orders.cargo_weight / 1000 : 0,
                    cargo_desc: selectedLoad.load_orders.cargo_desc,
                    trucksNeeded: selectedLoad.vehicles_needed,
                    distance: "N/A",
                    estimatedDuration: "N/A"
                  }}
                  totalTrucks={selectedLoad.vehicles_needed}
                  assignedTrucks={selectedLoad.vehicles_assigned}
                  availableTrucks={(selectedLoad.vehicles_needed || 0) - (selectedLoad.vehicles_assigned || 0)}
                  hasBeenAccepted={false}
                  currentResponse={carrierResponses[selectedLoad.id]}
                  onStartSelection={handleStartSelection}
                  onConfirmAcceptance={handleConfirmAcceptance}
                  onUpdateTrucks={handleUpdateTrucks}
                  onCancelSelection={handleCancelSelection}
                  context="accept-load"
                  isLoadingAccept={loadingOperations[`accept-${selectedLoad.id}`]}
                  badges={[
                    {
                      text: selectedLoad.load_orders.cargo_type,
                      variant: "outline" as const
                    }
                  ]}
                  // Dashboard view - only show accept button
                  showAcceptButton={true}
                  showCompleteButton={false}
                  showCancelButton={false}
                  showAvailableLabel={true}
                  showMyAssignments={true}
                  showRouteAvailabilityBar={true}
                  showRouteAssignedStatusBar={false}
                  showCarrierRouteStatusBar={false}
                  showFullyAssignedLoadBadge={true}
                  showActionButton={false}
                  showExpandedAcceptances={expandedAcceptances.has(selectedLoad.id)}
                  onToggleExpandedAcceptances={() => handleToggleExpandedAcceptances(selectedLoad.id)}
                  additionalDetails={undefined}
                />
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* View Active Load Modal */}
        <Dialog open={isViewActiveModalOpen} onOpenChange={setIsViewActiveModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-6">
            <DialogHeader className="pb-6 border-b border-border">
              <DialogTitle className="text-xl font-semibold">Active Load - Route Details</DialogTitle>
            </DialogHeader>
            
            {selectedActiveLoad && (() => {
              // Get aggregated assignment data for this route
              const routeSummary = getRouteAssignmentSummary(selectedActiveLoad.id);
              
              return (
                <div className="space-y-8 pt-6 px-2">
                  <LoadAcceptanceCard
                    id={selectedActiveLoad.id}
                    title={selectedActiveLoad.title}
                    subtitle={formatRoute(selectedActiveLoad.pickup_city, selectedActiveLoad.delivery_city)}
                    loadDetail={{
                      pickupCity: selectedActiveLoad.pickup_city,
                      dropCity: selectedActiveLoad.delivery_city,
                      pickupDate: formatAppDate(selectedActiveLoad.pickup_date, language),
                      vehicleType: selectedActiveLoad.vehicle_type || 'Truck',
                      weightTons: selectedActiveLoad.load_orders.cargo_weight ? selectedActiveLoad.load_orders.cargo_weight / 1000 : 0,
                      cargo_desc: selectedActiveLoad.load_orders.cargo_desc,
                      trucksNeeded: selectedActiveLoad.vehicles_needed,
                      distance: "N/A",
                      estimatedDuration: "N/A"
                    }}
                    // Use aggregated assignment data for view-order context
                    totalTrucks={routeSummary.totalVehiclesAssigned}
                    assignedTrucks={routeSummary.totalVehiclesAssigned}
                    availableTrucks={routeSummary.totalVehiclesActive}
                    trucksCompleted={routeSummary.totalVehiclesCompleted}
                    trucksCancelled={routeSummary.totalVehiclesCanceled}
                    hasBeenAccepted={true}
                    context="view-order"
                    onStartSelection={() => {}}
                    onConfirmAcceptance={() => {}}
                    onUpdateTrucks={() => {}}
                    onCancelSelection={() => {}}
                    onComplete={handleCompleteLoad}
                    onCancel={handleCancelLoad}
                    isLoadingComplete={loadingOperations[`complete-${selectedActiveLoad.id}`]}
                    isLoadingCancel={loadingOperations[`cancel-${selectedActiveLoad.id}`]}
                    badges={[
                      {
                        text: selectedActiveLoad.load_orders.cargo_type,
                        variant: "outline" as const
                      },
                      {
                        text: t('carrierDashboard.active'),
                        variant: "default" as const,
                        className: "bg-blue-100 text-blue-800 border-blue-200"
                      }
                    ]}
                    // Active load view - show complete/cancel buttons
                    showAcceptButton={false}
                    showCompleteButton={true}
                    showCancelButton={true}
                    showAvailableLabel={false}
                    showMyAssignments={false}
                    showRouteAvailabilityBar={false}
                    showRouteAssignedStatusBar={true}
                    showCarrierRouteStatusBar={false}
                    showFullyAssignedLoadBadge={false}
                    additionalDetails={undefined}
                  />
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};